import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { useTableStore } from "@/store/tableStore";
import { toast } from "@/hooks/use-toast";
import { StoreTable } from "@/models/tableModel";

interface StaffAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  table: StoreTable | null;
}

export function StaffAssignmentDialog({
  open,
  onOpenChange,
  table,
}: StaffAssignmentDialogProps) {
  const { assignStaffToTable } = useTableStore();
  const { employees, fetchEmployees } = useStoreManagementStore();
  const [selectedStaffId, setSelectedStaffId] = useState<string>("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (table?.store_id) {
      fetchEmployees(table.store_id);
      setSelectedStaffId(table.assigned_staff_id || "");
    }
  }, [table, fetchEmployees]);

  const handleAssignStaff = async () => {
    if (!table) return;

    setLoading(true);
    try {
      await assignStaffToTable(
        table.id,
        selectedStaffId === "none" ? null : selectedStaffId
      );

      toast({
        title: "Success",
        description:
          selectedStaffId === "none"
            ? "Staff assignment removed successfully"
            : "Staff member assigned successfully",
      });

      onOpenChange(false);
    } catch (error) {
      console.error("Error assigning staff:", error);
      toast({
        title: "Error",
        description: "Failed to assign staff member",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!table) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Staff to Table</DialogTitle>
          <DialogDescription>
            Assign a staff member to {table.name} (Table {table.number})
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="staff-select">Staff Member</Label>
            <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
              <SelectTrigger>
                <SelectValue placeholder="Select staff member (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No assignment</SelectItem>
                {employees.map((employee) => (
                  <SelectItem key={employee.id} value={employee.id}>
                    {employee.firstName} {employee.lastName} - {employee.role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button onClick={handleAssignStaff} disabled={loading}>
              {loading ? "Assigning..." : "Update Assignment"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
